import { Student } from '../types';
import { shouldShowSkillInPDF } from './dimensionFilters';
import { shouldShowDimensionAndSubdimensions } from './dimensionSeparationConfig';
import {
  resolveDescriptionKey,
  getDescriptionParagraphs,
} from '../data/subdimensionDescriptions';
import { autoconcienciaVoluntadDimension } from '../components/AutoconcienciaVoluntad';
import { habilidadesPensamientoDimension } from '../components/HabilidadesPensamiento';
import { habilidadesComunicacionalesDimension } from '../components/HabilidadesComunicacionales';
import { corporalidadMovimientoDimension } from '../components/CorporalidadyMovimiento';
import { lenguajesArtisticosDimension } from '../components/LenguajeArtistico';
import { motivacionAprendizajeDimension } from '../components/Motivacionaprendizaje';
import { encuentroNaturalezaDimension } from '../components/Encuentronaturaleza';
import { habilidadesSocialesDimension } from '../components/Habilidadessociales';
import { lenguaInglesaDimension } from '../components/LenguaInglesa';
import { amorDiosDimension } from '../components/AmordeDios';
import { pensamientoMatematicosDimension } from '../components/Pensamientomatematico';

interface Skill {
  id: number;
}

interface SubDimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface DimensionWithSubs {
  id: number;
  name: string;
  skills: Skill[];
  subDimensions?: SubDimension[];
}

interface DimensionEntry {
  id: number;
  name: string;
  skills: Skill[];
  dimension: DimensionWithSubs;
}

export interface SubdimensionDescriptionBlock {
  displayTitle: string;
  paragraphs: string[];
}

const getDimensionCategoriesForTenant = (
  tenantKey: string | null
): Record<string, string[]> => {
  const baseCategories: Record<string, string[]> = {
    'Exploración de uno mismo': [
      'Autoconciencia y voluntad',
      'Habilidades pensamiento',
      'Habilidades comunicacionales en el lenguaje español',
      'Corporalidad y Movimiento',
      'Lenguaje Artístico',
      'Motivación y disposición hacia el aprendizaje',
    ],
    'Exploración del mundo': [
      'Encuentro con la Naturaleza y Sociedad',
      'Habilidades de relación y conciencia social',
      'Pensamiento matemático',
      'Acercamiento a la lengua inglesa',
    ],
  };

  if (tenantKey === 'cpv.cl' || tenantKey === 'cpv_cl') {
    return {
      ...baseCategories,
      'Exploración de lo trascendente': ['Amor a Dios y sentido Cristiano'],
    };
  }

  return baseCategories;
};

const getAllDimensionsForStudent = (
  student: Student,
  tenantKey: string | null
): DimensionEntry[] => {
  const baseDimensions: DimensionEntry[] = [
    {
      id: 1,
      name: 'Autoconciencia y voluntad',
      skills: autoconcienciaVoluntadDimension?.skills ?? [],
      dimension: autoconcienciaVoluntadDimension as DimensionWithSubs,
    },
    {
      id: 2,
      name: 'Habilidades pensamiento',
      skills: habilidadesPensamientoDimension?.skills ?? [],
      dimension: habilidadesPensamientoDimension as DimensionWithSubs,
    },
    {
      id: 3,
      name: 'Habilidades comunicacionales en el lenguaje español',
      skills: habilidadesComunicacionalesDimension?.skills ?? [],
      dimension: habilidadesComunicacionalesDimension as DimensionWithSubs,
    },
    {
      id: 4,
      name: 'Corporalidad y Movimiento',
      skills: corporalidadMovimientoDimension?.skills ?? [],
      dimension: corporalidadMovimientoDimension as DimensionWithSubs,
    },
    {
      id: 5,
      name: 'Lenguaje Artístico',
      skills: lenguajesArtisticosDimension?.skills ?? [],
      dimension: lenguajesArtisticosDimension as DimensionWithSubs,
    },
    {
      id: 6,
      name: 'Motivación y disposición hacia el aprendizaje',
      skills: motivacionAprendizajeDimension?.skills ?? [],
      dimension: motivacionAprendizajeDimension as DimensionWithSubs,
    },
    {
      id: 7,
      name: 'Encuentro con la Naturaleza y Sociedad',
      skills: encuentroNaturalezaDimension?.skills ?? [],
      dimension: encuentroNaturalezaDimension as DimensionWithSubs,
    },
    {
      id: 8,
      name: 'Habilidades de relación y conciencia social',
      skills: habilidadesSocialesDimension?.skills ?? [],
      dimension: habilidadesSocialesDimension as DimensionWithSubs,
    },
    {
      id: 9,
      name: 'Pensamiento matemático',
      skills: pensamientoMatematicosDimension?.skills ?? [],
      dimension: pensamientoMatematicosDimension as DimensionWithSubs,
    },
    {
      id: 10,
      name: 'Acercamiento a la lengua inglesa',
      skills: lenguaInglesaDimension?.skills ?? [],
      dimension: lenguaInglesaDimension as DimensionWithSubs,
    },
  ];

  if (tenantKey === 'cpv.cl' || tenantKey === 'cpv_cl') {
    baseDimensions.push({
      id: 11,
      name: 'Amor a Dios y sentido Cristiano',
      skills: amorDiosDimension?.skills ?? [],
      dimension: amorDiosDimension as DimensionWithSubs,
    });
  }

  return baseDimensions
    .map((dim) => ({
      ...dim,
      skills:
        dim.skills?.filter((skill) =>
          shouldShowSkillInPDF(skill.id, dim.id, student.course)
        ) ?? [],
      dimension: {
        ...dim.dimension,
        skills:
          dim.dimension?.skills?.filter((skill) =>
            shouldShowSkillInPDF(skill.id, dim.id, student.course)
          ) ?? [],
      },
    }))
    .filter(
      (dim) =>
        dim.skills &&
        Array.isArray(dim.skills) &&
        dim.dimension &&
        dim.skills.length > 0
    );
};

export const getVisibleSubdimensionDescriptionsForPdf = (
  student: Student,
  tenantKey: string | null
): SubdimensionDescriptionBlock[] => {
  const dimensionCategories = getDimensionCategoriesForTenant(tenantKey);
  const allDimensions = getAllDimensionsForStudent(student, tenantKey);
  const results: SubdimensionDescriptionBlock[] = [];
  const usedDescriptionKeys = new Set<string>();

  const addBlock = (displayTitle: string) => {
    const descriptionKey = resolveDescriptionKey(displayTitle);
    if (!descriptionKey || usedDescriptionKeys.has(descriptionKey)) {
      return;
    }

    const paragraphs = getDescriptionParagraphs(descriptionKey);
    if (paragraphs.length === 0) {
      return;
    }

    usedDescriptionKeys.add(descriptionKey);
    results.push({ displayTitle, paragraphs });
  };

  Object.values(dimensionCategories).forEach((dimensionNames) => {
    dimensionNames.forEach((dimensionName) => {
      const dimension = allDimensions.find((d) => d.name === dimensionName);
      if (!dimension) {
        return;
      }

      const separationConfig = shouldShowDimensionAndSubdimensions(
        dimension.id,
        student.course
      );

      if (separationConfig.showMother) {
        addBlock(dimension.name);
      }

      if (
        separationConfig.showChildren &&
        dimension.dimension?.subDimensions &&
        Array.isArray(dimension.dimension.subDimensions) &&
        dimension.dimension.subDimensions.length > 0
      ) {
        const validSubdimensions = dimension.dimension.subDimensions.filter(
          (subDim) => {
            if (!subDim?.skills || !Array.isArray(subDim.skills)) {
              return false;
            }
            const validSkillIds = subDim.skills
              .map((s) => s.id)
              .filter((skillId) =>
                shouldShowSkillInPDF(skillId, dimension.id, student.course)
              );
            return validSkillIds.length > 0;
          }
        );

        validSubdimensions.forEach((subDim) => {
          addBlock(subDim.name);
        });
      }
    });
  });

  return results;
};
